import { ClaimsIndex } from './ClaimsIndex';
import { ClaimsFilterState } from '../types/claimsFilters';
import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import { getNormalizedStatus } from './ClaimUtils';

export type SortField =
  | 'serviceType'
  | 'providerName'
  | 'location'
  | 'status'
  | 'dateOfService'
  | 'amountOwed';

export type SortOrder = 'asc' | 'desc';

export interface ClaimsQuery {
  sortBy?: SortField;
  sortOrder?: SortOrder;
  filters?: ClaimsFilterState;
}

function normalize(str: unknown): string {
  return typeof str === 'string' ? str.trim().toLowerCase() : '';
}

function getServiceType(claim: Claim): string {
  const coding = claim.type?.coding;
  if (!coding) return '';
  
  if (Array.isArray(coding)) {
    return String(coding[0]?.display || '').trim();
  }
  
  if (typeof coding === 'object' && 'display' in coding) {
    return String((coding as any).display || '').trim();
  }
  
  return '';
}

function getProviderName(claim: Claim): string {
  const ext = claim.extension?.find((e: any) => e?.url === 'providerName');
  return String(ext?.value || '').trim();
}

function getLocation(claim: Claim): string {
  const ext = claim.extension?.find((e: any) => e?.url === 'practiceName');
  return String(ext?.value || '').trim();
}

function getStatus(claim: Claim): string {
  return getNormalizedStatus(String(claim.status || ''));
}

function getDateOfService(claim: Claim): number {
  const dateStr = (claim as any).normalizedServiceDate ?? String(claim.created || '');
  const ts = new Date(dateStr).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function getAmountOwed(claim: Claim): number {
  if (!Array.isArray(claim.total)) return 0;

  const bucket = claim.total.find(
    (t: any) => t?.category?.coding?.code === 'totalPatientResponsibility'
  );

  const raw = bucket?.amount?.value;
  if (raw == null) return 0;

  const parsed = Number(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function applyFilters(
  claims: Claim[],
  filters?: ClaimsFilterState
): Claim[] {
  if (!filters) return claims;

  let filtered = claims;

  if (filters.selectedServiceTypes?.length) {
    const types = new Set(filters.selectedServiceTypes.map(normalize));
    filtered = filtered.filter(claim => {
      const serviceType = getServiceType(claim);
      if (serviceType.includes('|')) {
        return serviceType.split('|').some(part => types.has(normalize(part)));
      }
      return types.has(normalize(serviceType));
    });
  }

  if (filters.selectedStatus?.length) {
    const statuses = new Set(filters.selectedStatus.map(normalize));
    filtered = filtered.filter(claim => 
      statuses.has(normalize(getStatus(claim)))
    );
  }

  if (filters.selectedLocations?.length) {
    const locations = new Set(filters.selectedLocations.map(normalize));
    filtered = filtered.filter(claim => 
      locations.has(normalize(getLocation(claim)))
    );
  }

  if (filters.selectedProviders?.length) {
    const providers = new Set(filters.selectedProviders.map(normalize));
    filtered = filtered.filter(claim => 
      providers.has(normalize(getProviderName(claim)))
    );
  }

  if (filters.customFromDate || filters.customToDate || filters.selectedDate) {
    filtered = filterByDateRange(
      filtered,
      filters.customFromDate,
      filters.customToDate,
      filters.selectedDate
    );
  }

  if (filters.amountMin || filters.amountMax) {
    filtered = filterByAmountRange(filtered, filters.amountMin, filters.amountMax);
  }

  return filtered;
}

function filterByDateRange(
  claims: Claim[],
  from?: string,
  to?: string,
  selected?: string
): Claim[] {
  let start = 0;
  let end = Date.now();

  if (from) start = new Date(from.trim()).getTime();
  if (to) end = new Date(to.trim()).getTime();

  if (selected && !from && !to) {
    const now = Date.now();
    if (selected === 'last30') start = now - 30 * 86400000;
    if (selected === 'last60') start = now - 60 * 86400000;
    if (selected === 'thisYear') {
      start = new Date(new Date().getFullYear(), 0, 1).getTime();
    }
  }

  if (Number.isNaN(start) || Number.isNaN(end) || start > end) return [];

  return claims.filter(claim => {
    const d = getDateOfService(claim);
    return Number.isFinite(d) && d >= start && d <= end;
  });
}

function filterByAmountRange(
  claims: Claim[],
  minStr?: string,
  maxStr?: string
): Claim[] {
  const min = minStr ? Number(minStr.trim()) : 0;
  const max = maxStr ? Number(maxStr.trim()) : Infinity;

  if (Number.isNaN(min) || Number.isNaN(max) || min > max) return [];

  return claims.filter(claim => {
    const amt = getAmountOwed(claim);
    return Number.isFinite(amt) && amt >= min && amt <= max;
  });
}

function defaultSortDirection(field: SortField): SortOrder {
  return field === 'dateOfService' || field === 'amountOwed' ? 'desc' : 'asc';
}

function applySorting(
  claims: Claim[],
  sortBy?: SortField,
  sortOrder?: SortOrder
): Claim[] {
  const field = sortBy ?? 'dateOfService';
  const order = sortOrder ?? defaultSortDirection(field);

  const sorted = [...claims];

  if (field === 'dateOfService') {
    sorted.sort((a, b) => {
      const dateA = getDateOfService(a);
      const dateB = getDateOfService(b);
      return order === 'asc' ? dateA - dateB : dateB - dateA;
    });
  } else if (field === 'amountOwed') {
    sorted.sort((a, b) => {
      const amtA = getAmountOwed(a);
      const amtB = getAmountOwed(b);
      return order === 'asc' ? amtA - amtB : amtB - amtA;
    });
  } else if (field === 'status') {
    sorted.sort((a, b) => {
      const statusA = getStatus(a);
      const statusB = getStatus(b);
      return order === 'asc' 
        ? statusA.localeCompare(statusB) 
        : statusB.localeCompare(statusA);
    });
  } else {
    sorted.sort((a, b) => {
      let valueA = '';
      let valueB = '';
      
      if (field === 'serviceType') {
        valueA = normalize(getServiceType(a));
        valueB = normalize(getServiceType(b));
      } else if (field === 'providerName') {
        valueA = normalize(getProviderName(a));
        valueB = normalize(getProviderName(b));
      } else if (field === 'location') {
        valueA = normalize(getLocation(a));
        valueB = normalize(getLocation(b));
      }
      
      if (!valueA && valueB) return 1;
      if (!valueB && valueA) return -1;
      return order === 'asc'
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA);
    });
  }

  return sorted;
}

export function queryClaims(
  index: ClaimsIndex,
  query: ClaimsQuery = {}
): Claim[] {
  const allClaims = Array.from(index.byId.values());
  const filtered = applyFilters(allClaims, query.filters);
  return applySorting(filtered, query.sortBy, query.sortOrder);
}

export function paginateResults(
  claims: Claim[],
  page: number,
  size: number
): Claim[] {
  const start = page * size;
  return claims.slice(start, start + size);
}
